/**
 * Route and Tab Configuration
 * Defines all routes with their tabs/subroutes for RBAC Management
 * Auto-generated from siteMaps.js structure
 */
import { LEAD_DETAILS_COMPONENTS } from './leadDetailsComponents';
import { getCurrentUserRole } from 'modules/utilities/utility';

/**
 * Auto-generate URL paths from hierarchy
 * @param {string} parentPath - Parent route path (e.g., "/configure")
 * @param {string} childKey - Child key (e.g., "discount-codes")
 * @param {string} type - 'subRoute', 'tab', or 'subTab'
 * @param {string} [parentTabKey] - Parent tab key (for subTabs only)
 */
const generatePath = (parentPath, childKey, type, parentTabKey = null) => {
  if (type === 'subRoute') return `${parentPath}/${childKey}`;
  if (type === 'tab') return `${parentPath}?tab=${childKey}`;
  if (type === 'subTab') return `${parentPath}?tab=${parentTabKey}&subtab=${childKey}`;
  return parentPath;
};

export const ROUTES_TAB_CONFIG = {
  // ===== MAIN DASHBOARD & TICKETS =====
  Dashboard: {
    label: "Dashboard",
    icon: "chart-pie",
    routes: {
      "/dashboard": {
        name: "/dashboard",
        label: "Dashboard",
        rbacEnabled: true,
        componentPath: "modules/dashboards/Dashboard",
        tabs: [
          { key: "home", name: "Home", order: 0, rbacEnabled: true },
          { key: "revenue-report", name: "Revenue Report", order: 1, rbacEnabled: true },
          { key: "actionable", name: "Actionable", order: 2, rbacEnabled: true },
          { key: "slot-view", name: "Slot View", order: 3, rbacEnabled: true }
        ],
        subRoutes: []
      }
    }
  },
  MyTickets: {
    label: "My Tickets",
    icon: "ticket-alt",
    routes: {
      "/my-tickets": {
        name: "/my-tickets",
        label: "My Tickets",
        rbacEnabled: true,
        componentPath: "pages/MyTickets",
        tabs: [
          { key: "assigned", name: "Assigned Tickets", order: 0 },
          { key: "internalTickets", name: "Internal Tickets", order: 1 },
          { key: "all", name: "All Tickets", order: 2 },
          { key: "stats", name: "Stats", order: 3 },
          { key: "ticketing-dashboard", name: "Ticketing Dashboard", order: 4 },
          { key: "failed-ticket", name: "Failed Ticket", order: 5 },
          { key: "cx-complaint-slot", name: "Customer Complaint Slots", order: 6 },
          { key: "map-missing-ticket", name: "Map Missing Ticket", order: 7 },
          { key: "ticket-overview", name: "Ticket Overview", order: 8 },
          {
            key: "update-ticket",
            name: "Update Ticket",
            order: 9,
            tabs: [
              { key: "internal", name: "Internal", order: 0 },
              { key: "customer-facing", name: "Customer Facing", order: 1 }
            ]
          }
        ]
      }
    }
  },

  // ===== CASE & ORDER MANAGEMENT =====
  CaseManagement: {
    label: "Case Management",
    icon: "video",
    routes: {
      "/case-management": {
        name: "/case-management",
        label: "Case Management",
        rbacEnabled: true,
        subRoutes: [
          { key: "new-leads", name: "New Leads", order: 0, componentPath: "modules/case_management/AllLeads", props: { variant: "new-leads" } },
          { key: "all-leads", name: "All Leads", order: 1, componentPath: "modules/case_management/AllLeads", props: { variant: "all-leads" } },
          { key: "all-cases", name: "All Cases", order: 2, componentPath: "modules/case_management/AllLeads", props: { variant: "all-cases" } },
          { key: "vayu-lead", name: "Vayu Lead", order: 3, componentPath: "modules/vayu_lead/VayuLead" },
          { key: "all-cases-tag", name: "All Cases Tags", order: 4, componentPath: "modules/case_management/AllLeadsTag" },
          { key: "slot-booking", name: "Slot Booking", order: 5, componentPath: "modules/case_management/CaseSlotBooking" },
          {
            // Virtual RBAC entry — the actual /lead-details/:id route is still registered
            // under SharedRoutes for runtime routing. This entry exists ONLY so the RBAC UI
            // can surface the component-level toggles for lead-details under CaseManagement.
            //
            // - rbacEnabled: false      → page is always accessible; route-level toggle is a no-op
            // - hasComponentRBAC: true  → tells RoutePermissionsWizard to render this row + Components drawer
            // - sidebarHidden: true     → tells buildSidebarRoutes to skip (don't show in left nav)
            // - components              → the registry of per-feature toggles (see leadDetailsComponents.js)
            key: "lead-details",
            name: "Lead Details",
            order: 6,
            rbacEnabled: false,
            hasComponentRBAC: true,
            sidebarHidden: true,
            components: LEAD_DETAILS_COMPONENTS,
          },
        ]
      }
    }
  },

  CustomerData: {
    label: "Customer Data",
    icon: "database",
    routes: {
      "/customer-data": {
        name: "/customer-data",
        label: "Customer Data",
        rbacEnabled: true,
        componentPath: "pages/CustomerDataPage",
        tabs: [
          { key: "table", name: "Customer Data", order: 0 },
          { key: "cohort", name: "Retention Cohort Analysis", order: 1 },
          { key: "reorder-cohort", name: "Reorder Cohort Analysis", order: 2 }
        ]
      }
    }
  },

  // OrderManagement and AllOrderManagement are hidden — their routes are covered
  // by AdminOrderManagement subroutes (Transfer Order, All Orders).
  // Kept for direct route registration (React Router) but hidden from sidebar.
  OrderManagement: {
    label: "Order Management",
    icon: "users",
    routes: {
      "/order-management": {
        name: "/order-management",
        label: "Order Management",
        rbacEnabled: true,
        componentPath: "modules/order_management/OrderMovement",
        subRoutes: []
      }
    }
  },

  AllOrderManagement: {
    label: "All Orders",
    icon: "users",
    routes: {
      "/all-orders": {
        name: "/all-orders",
        label: "All Orders",
        rbacEnabled: true,
        componentPath: "modules/order_management/AllOrdersManagement",
        subRoutes: []
      }
    }
  },

  AdminOrderManagement: {
    label: "Order Management",
    icon: "envelope-open",
    routes: {
      "/admin-order-management": {
        name: "/admin-order-management",
        label: "Order Management",
        rbacEnabled: true,
        subRoutes: [
          { key: "transfer-order", name: "Transfer Order", order: 0, componentPath: "modules/order_management/OrderMovement" },
          { key: "all-orders", name: "All Orders", order: 1, componentPath: "modules/order_management/AllOrdersManagement" },
          { key: "update-orders", name: "Update Orders", order: 2, componentPath: "pages/UpdateOrders" },
          { key: "open-orders", name: "Open Orders", order: 3, componentPath: "pages/OpenOrders" },
          { key: "ghost-orders", name: "Ghost Orders", order: 4, componentPath: "pages/GhostOrders" },
          { key: "assigned-doctors", name: "Assigned Doctors", order: 5, componentPath: "pages/AssignedDoctors" },
          { key: "return-orders", name: "Return Orders", order: 6, componentPath: "modules/order_management/ReturnOrders" },
          { key: "cod-to-prepaid", name: "COD to Prepaid", order: 7, componentPath: "pages/CodToPrepaid" },
          { key: "pre-dispatch-cod-to-prepaid", name: "Pre Dispatch COD to Prepaid", order: 8, componentPath: "pages/PreDispatchCodToPrepaid" },
          { key: "ots-mapper", name: "OTS Mapper", order: 9, componentPath: "modules/Ots_mangment/OTSManagment" },
          { key: "ndr-mapper", name: "NDR Mapper", order: 10, componentPath: "modules/Ndr_management/NDRManagement" },
          { key: "ndr-mapper-master", name: "NDR Mapper Master", order: 11, componentPath: "modules/Ndr_management_master/NDRManagementMaster" },
          { key: "offline-orders", name: "Offline Orders", order: 12, componentPath: "modules/offline_orders/OfflineOrders" },
          { key: "refund-management", name: "Refund Management", order: 13, componentPath: "modules/refund_management/RefundManagement" },
          { key: "crm-prepaid", name: "CRM Prepaid", order: 14, componentPath: "pages/CrmPrepaid", defaultRoles: ["admin", "super_admin"] }
        ]
      }
    }
  },

  TransferCustomer: {
    label: "Transfer Customer",
    icon: "exchange-alt",
    routes: {
      "/admin-case-management": {
        name: "/admin-case-management",
        label: "Transfer Customer",
        rbacEnabled: true,
        componentPath: "pages/AdminCaseManagement",
        tabs: [
          { key: "transfer_customer", name: "Transfer Customer", order: 0 },
          { key: "recent_transfer", name: "Recent Transfer", order: 1 },
          { key: "transfer_customer_upload", name: "Transfer Customer Upload", order: 2 }
        ]
      }
    }
  },

  UpdateOrders: {
    label: "Update Orders",
    icon: "edit",
    routes: {
      "/order-management/update_order": {
        name: "/order-management/update_order",
        label: "Update Orders",
        rbacEnabled: true,
        componentPath: "pages/UpdateOrders",
        tabs: [
          { key: "update-order", name: "Update Order", order: 0 },
          { key: "update-order-history", name: "Update Order History", order: 1 }
        ]
      }
    }
  },

  // ===== CONFIGURATION & SETTINGS =====
  Configure: {
    label: "Configure",
    icon: "tools",
    routes: {
      "/configure": {
        name: "/configure",
        label: "Configure",
        rbacEnabled: true,  // RBAC Enabled - subroutes filtered by permission
        subRoutes: [
          { key: "medicines", name: "Medicines", rbacEnabled: true, order: 0, componentPath: "pages/Medicines" },
          { key: "tags", name: "Tags", rbacEnabled: true, order: 1, componentPath: "pages/Tags" },
          { key: "product-mapping", name: "Product Mapping", rbacEnabled: true, order: 2, componentPath: "pages/ProductMapping" },
          { key: "settings", name: "Settings", rbacEnabled: true, order: 3, componentPath: "pages/Settings" },
          { key: "customer-computed-data", name: "Customer Data", rbacEnabled: true, order: 4, componentPath: "pages/CustomerComputedDataPage" },
          { key: "offline-hairtest", name: "Offline Hair Test Config", rbacEnabled: true, order: 5, componentPath: "pages/OfflineHairTestConfig" },
          { key: "discount-codes", name: "Discount Code", rbacEnabled: true, order: 6, componentPath: "pages/DiscountCodes" },
          { key: "rbac-management", name: "RBAC Management", rbacEnabled: true, order: 7, componentPath: "pages/RBACConfigure" },
          { key: "bulk-kit-cohort-config", name: "Bulk Kit Cohort Config", rbacEnabled: true, order: 8, componentPath: "pages/OfflineBulkKitCohortConfig" },
          {
            key: "ticket-group-management",
            name: "Ticket Group Management",
            rbacEnabled: true,
            order: 9,
            componentPath: "pages/TicketGroups",
            tabs: [
              { key: "subcategories", name: "Subcategories", order: 0 },
              { key: "groups", name: "Groups", order: 1 },
              { key: "groups-activity-logs", name: "Groups Activity Logs", order: 2 }
            ]
          },
          {
            key: "clinic-management",
            name: "Clinic Management",
            rbacEnabled: true,
            order: 10,
            componentPath: "modules/clinic-management/Clinics",
            tabs: [
              { key: "add-clinic", name: "Add a Clinic", order: 0 },
              { key: "all-clinic", name: "All Clinic", order: 1 },
              { key: "edit-clinic", name: "Edit a Clinic", order: 2 }
            ]
          },
          { key: "offline-doctor-roaster", name: "Offline Doctor Roaster", rbacEnabled: true, order: 11, componentPath: "pages/OfflineDoctorRoaster" },
          { key: "webinar-management", name: "Webinars", rbacEnabled: true, order: 12, componentPath: "pages/webinar" },
          {
            key: "ticket-configure",
            name: "Ticket Configure",
            rbacEnabled: true,
            order: 13,
            componentPath: "pages/TicketConfigure",
            tabs: [
              { key: "ticket_configure", name: "Configuration", order: 0 },
              { key: "manage_assignee", name: "Manage Assignees", order: 1 }
            ]
          }
        ]
      }
    }
  },

  UserManagement: {
    label: "User Management",
    icon: "tasks",
    routes: {
      "/user-management": {
        name: "/user-management",
        label: "User Management",
        rbacEnabled: true,
        componentPath: "modules/user_management/UserManagement",
        tabs: [
          {
            key: "user-mgmt",
            name: "User Management",
            order: 0,
            tabs: [
              { key: "user-details", name: "User Details", order: 0 },
              { key: "doctor-configuration", name: "Doctor Configuration", order: 1 },
              { key: "audit-log", name: "Audit Log", order: 2 },
              { key: "permissions", name: "Permissions", order: 3 }
            ]
          },
          { key: "group-management", name: "Group Management", order: 1 },
          { key: "login-report", name: "Login Report", order: 2 },
          {
            key: "call-provider-mgmt",
            name: "Call Provider Management",
            order: 3,
            tabs: [
              { key: "user-call-provider", name: "Users Call Provider", order: 0 },
              { key: "call-providers", name: "Call Providers", order: 1 }
            ]
          }
        ]
      },
    }
  },

  DeviceMonitoring: {
    label: "Device Monitoring",
    icon: "desktop",
    routes: {
      "/device-monitoring": {
        name: "/device-monitoring",
        label: "Device Monitoring",
        rbacEnabled: true,
        componentPath: "modules/device_monitoring/DeviceMonitoring",
        subRoutes: []
      }
    }
  },

  // ===== REPORTING & ANALYTICS =====
  Reports: {
    label: "Reports",
    icon: "envelope-open",
    routes: {
      "/reports": {
        name: "/reports",
        label: "Reports",
        rbacEnabled: true,
        componentPath: "pages/Reports",
        tabs: [
          { key: "active-customer-report", name: "Active Customer Report", order: 0 },
          { key: "weekly-answered-report", name: "Weekly Answered Report", order: 1 },
          { key: "unordered-report", name: "Unordered Report", order: 2 },
          { key: "engagement-feedback-report", name: "Engagement Feedback Report", order: 3 },
          { key: "engagement-report", name: "Engagement Report", order: 4 },
          { key: "adherence-report", name: "Adherence Report", order: 5 },
          { key: "sales-report", name: "Sales Report", order: 6 },
          { key: "order-link-report", name: "Order Link Report", order: 7 }
        ]
      }
    }
  },

  SalesReport: {
    label: "Sales Report",
    icon: "chart-line",
    routes: {
      "/sales-report": {
        name: "/sales-report",
        label: "Sales Report",
        rbacEnabled: true,
        subRoutes: [
          { key: "new-sales-charts", name: "New Sales Charts", order: 0, componentPath: "pages/NewSalesDashboard" },
          { key: "sales-charts", name: "Sales Charts", order: 1, componentPath: "pages/FinanceDashboard" },
          { key: "report-charts", name: "Report Charts", order: 2, componentPath: "modules/Shopify_dashboard/ReportDashboard" },
          { key: "month-report", name: "Month Report", order: 3, componentPath: "modules/mtd_report/MonthReport" },
          { key: "sales-data", name: "Sales Data", order: 4, componentPath: "pages/SalesDashboard" },
          { key: "utm-campaign", name: "Utm Campaign", order: 5, componentPath: "pages/UtmCampaign" },
          { key: "revenue-tracking", name: "Revenue Tracking", order: 6, componentPath: "pages/RevenueTracker" },
          { key: "transactions-dashboard", name: "Transaction and Payment Gateway", order: 7, componentPath: "modules/payment_gateway/PayementGateway" },
          { key: "checkout-cart", name: "Checkout Cart", order: 8, componentPath: "pages/CheckoutSuccessRatePage" },
          { key: "web-funnel-dashboard", name: "Web Funnel Dashboard", order: 10, componentPath: "modules/funnel_dashboard" },
          { key: "inventory-tracker", name: "Inventory Tracker", order: 11, componentPath: "pages/InventoryTracker" }
        ]
      }
    }
  },

  OperationManagement: {
    label: "Operation Management",
    icon: "globe",
    routes: {
      "/operation-management": {
        name: "/operation-management",
        label: "Operation Management",
        rbacEnabled: true,
        subRoutes: [
          {
            key: "warehouse", name: "Warehouse", order: 0, componentPath: "pages/Warehouse",
            tabs: [
              { key: "stock", name: "Stock", order: 0 },
              { key: "warehouse-inwards", name: "Warehouse Inwards", order: 1 },
              { key: "warehouse-outwards", name: "Warehouse OutWards", order: 2 },
              { key: "outward-intransit", name: "Outward Intransit", order: 3 },
              { key: "manual-updation", name: "Manual Updation", order: 4 },
              { key: "warehouse-supplier", name: "Warehouse Supplier", order: 5 },
              { key: "warehouse-report", name: "Warehouse Report", order: 6 }
            ]
          },
          {
            key: "invoice", name: "Invoice & Credit Note", order: 1, componentPath: "pages/InvoiceGenerate",
            tabs: [
              { key: "invoice", name: "Generate Invoice", order: 0 },
              { key: "credit", name: "Generate Credit Note", order: 1 },
              { key: "download", name: "Download Invoice Report", order: 2 },
              { key: "downloadCaseOrderCount", name: "Cases Assignment Order Count Wise Report", order: 3 }
            ]
          },
          {
            key: "misc-orders", name: "Misc Orders", order: 2, componentPath: "pages/oda/components/ODA",
            tabs: [
              { key: "allocated-orders", name: "Allocated Orders", order: 0 },
              { key: "b2b-orders", name: "B2B Orders", order: 1 },
              { key: "flagged-invoice", name: "Flagged Invoice", order: 2 },
              { key: "flagged-orders", name: "Flagged Orders", order: 3 },
              { key: "international-orders", name: "International Orders", order: 4 },
              { key: "onhold-orders", name: "OnHold Orders", order: 5 },
              { key: "other-orders", name: "Other Orders", order: 6 },
              { key: "prozo-pending-orders", name: "Prozo Pending Orders", order: 7 },
              { key: "pending-for-shipping", name: "Pending for Shipping", order: 8 },
              { key: "delivery-flagged-orders", name: "Delivery Flagged Orders", order: 9 }
            ]
          },
          {
            key: "reports", name: "Reports", order: 3, componentPath: "pages/Reports",
            tabs: [
              { key: "active-customer-report", name: "Active Customer Report", order: 0 },
              { key: "weekly-answered-report", name: "Weekly Answered Report", order: 1 },
              { key: "unordered-report", name: "Unordered Report", order: 2 },
              { key: "engagement-feedback-report", name: "Engagement Feedback Report", order: 3 },
              { key: "engagement-report", name: "Engagement Report", order: 4 },
              { key: "adherence-report", name: "Adherence Report", order: 5 },
              { key: "sales-report", name: "Sales Report", order: 6 },
              { key: "order-link-report", name: "Order Link Report", order: 7 }
            ]
          },
          { key: "zoho", name: "Zoho", order: 4, componentPath: "modules/zoho/Zoho" },
          { key: "uni-commerce", name: "UniCommerce Dashboard", order: 5, componentPath: "modules/unicommerce/FailedUniOrdersTable" },
          { key: "warehouse-pincode", name: "Warehouse Pincode", order: 6, componentPath: "pages/WarehousePincodeMapping" },
          { key: "bulk-upload-delhivery", name: "Bulk Upload Delhivery", order: 7, componentPath: "modules/third_party_upload/UploadCsvFutwork" },
          { key: "refund-tracking", name: "Refund Tracking", order: 8, componentPath: "pages/RefundTracking" },
          { key: "na-ndr-orders", name: "NA NDR Orders", order: 9, componentPath: "pages/NaNdrOrders" },
          { key: "unshipped-orders", name: "Unshipped Orders", order: 10, componentPath: "pages/UnshippedOrders" },
          { key: "control-tower", name: "Control Tower", order: 11, componentPath: "pages/ControlTower" },
          // Route stays registered (reachable via the siren button on the Control Tower
          // dashboard) but is hidden from the left nav — no standalone sidebar tab.
          { key: "delayed-comm", name: "Delayed Comm", order: 12, componentPath: "pages/DelayedComm", sidebarHidden: true },
          { key: "csat-summary", name: "CSAT Summary", order: 13, componentPath: "pages/CsatSummaryPage" }
        ]
      }
    }
  },

  // ===== ENGAGEMENT & FEEDBACK =====
  EngagementReport: {
    label: "Engagement Reports",
    icon: "table",
    routes: {
      "/engagement-report": {
        name: "/engagement-report",
        label: "Engagement Reports",
        rbacEnabled: true,
        componentPath: "pages/EngagementRep",
        tabs: [
          { key: "slot_booking", name: "Slot Booking", order: 0 },
          { key: "slot_booking_total", name: "Slot Booking Total", order: 1 },
          { key: "ivr_booking", name: "Ivr Engagements", order: 2 },
          { key: "prescription_call", name: "Prescription Call", order: 3 },
          { key: "week_1", name: "Week #1", order: 4 },
          { key: "week_3", name: "Week #3", order: 5 },
          { key: "retention", name: "Retention", order: 6 },
          { key: "adhoc_call", name: "Adhoc Call", order: 7 },
          { key: "concern_call", name: "Concern Call", order: 8 },
          { key: "SLOT_BOOKING_MEDICAL", name: "Slot Booking Medical", order: 9 },
          { key: "abandoned_cart", name: "Abandon Cart", order: 10 },
          { key: "repeat_retention", name: "Repeat Retention", order: 11 },
          { key: "w0_call", name: "W0-Call", order: 12 },
          { key: "forced_slot", name: "Forced-Slot", order: 13 },
          { key: "kit_not_started", name: "Kit Not Started", order: 14 },
          { key: "scalpie_update", name: "Scalpie Update", order: 15 },
          { key: "customer_complaint_booking", name: "Customer Complaint Booking", order: 16 },
          { key: "chat_slot_booking", name: "Chat Slot Booking", order: 17 }
        ]
      }
    }
  },

  CustomerFeedback: {
    label: "Customer Feedback",
    icon: "comments",
    routes: {
      "/customer-feedback": {
        name: "/customer-feedback",
        label: "Customer Feedback",
        rbacEnabled: true,
        componentPath: "modules/support_feedback/Feedback",
        subRoutes: []
      }
    }
  },

  SupportFeedback: {
    label: "Feedback",
    icon: "comments",
    routes: {
      "/feedback": {
        name: "/feedback",
        label: "Feedback",
        rbacEnabled: true,
        componentPath: "modules/support_feedback/FeedbackTab",
        tabs: [
          { key: "Build-a-Plan", name: "Build a Plan", order: 0 },
          { key: "CSAT-Feedback", name: "CSAT Feedback", order: 1 },
          { key: "Ticket-Feedback", name: "Ticket Feedback", order: 2 },
          { key: "Delivery-Feedback", name: "Delivery Feedback", order: 3 },
          { key: "Treatment-Feedback", name: "Treatment Feedback", order: 4 },
          { key: "Female-Survey", name: "Female Survey", order: 5 },
          { key: "Community-NPS", name: "Community NPS", order: 6 },
          { key: "Store-Feedback", name: "Store Feedback", order: 7 }
        ]
      }
    }
  },

  ManageWeeklyFeedback: {
    label: "Manage Weekly Feedback",
    icon: "calendar-alt",
    routes: {
      "/manage-weekly-schedules": {
        name: "/manage-weekly-schedules",
        label: "Manage Weekly Feedback",
        rbacEnabled: true,
        componentPath: "pages/ManageWeekly",
        subRoutes: []
      }
    }
  },

  // ===== DOCTOR & MEDICAL =====
  DoctorBooking: {
    label: "Doctor",
    icon: "user-plus",
    routes: {
      "/doctor-booking": {
        name: "/doctor-booking",
        label: "Doctor Booking",
        rbacEnabled: true,
        componentPath: "pages/DoctorBooking",
        tabs: [],
        subRoutes: [
          { key: "booking", name: "Doctor Booking", order: 0, componentPath: "pages/DoctorBooking", tabs: [
            { key: "list-view", name: "List View", order: 0 },
            { key: "calendar-view", name: "Calendar View", order: 1 }
          ]},
          { key: "roaster", name: "Offline Doctor Roaster", order: 1, componentPath: "pages/OfflineDoctorRoaster" },
          { key: "prescription-approval", name: "Prescription Approval", order: 2, componentPath: "pages/ApprovePrescription" }
        ]
      }
    }
  },

  MedicalEscalationTickets: {
    label: "Medical Escalation Tickets",
    icon: "tag",
    routes: {
      "/medical-escalation": {
        name: "/medical-escalation",
        label: "Medical Escalation Tickets",
        rbacEnabled: true,
        componentPath: "modules/my_tickets/MedicalEscalationTickets",
        subRoutes: []
      }
    }
  },

  PrescribedPatients: {
    label: "Prescribed Patients",
    icon: "users",
    routes: {
      "/prescribed-patients": {
        name: "/prescribed-patients",
        label: "Prescribed Patients",
        rbacEnabled: true,
        componentPath: "pages/PrescribedPatients",
        subRoutes: []
      }
    }
  },

  PrescriptionApproval: {
    label: "Prescription Approval",
    icon: "envelope-open",
    routes: {
      "/prescription-approval": {
        name: "/prescription-approval",
        label: "Prescription Approval",
        rbacEnabled: true,
        componentPath: "pages/ApprovePrescription",
        subRoutes: []
      }
    }
  },

  // ===== DASHBOARDS =====
  AdminDashboard: {
    label: "Admin Dashboard",
    icon: "home",
    routes: {
      "/admin-dashboard": {
        name: "/admin-dashboard",
        label: "Admin Dashboard",
        rbacEnabled: true,
        componentPath: "pages/AdminDashboard",
        subRoutes: []
      }
    }
  },

  PerformanceDashboard: {
    label: "Performance Dashboard",
    icon: "table",
    routes: {
      "/performance-dashboard": {
        name: "/performance-dashboard",
        label: "Performance Dashboard",
        rbacEnabled: true,
        componentPath: "pages/PerformanceDashboard",
        subRoutes: []
      }
    }
  },

  VoiceBotAnalytics: {
    label: "Voice Bot Analytics",
    icon: "chart-line",
    routes: {
      "/voice-bot-analytics": {
        name: "/voice-bot-analytics",
        label: "Voice Bot Analytics",
        rbacEnabled: true,
        defaultRoles: ["super_admin", "quality"],
        componentPath: "pages/VoiceBotAnalytics",
        subRoutes: []
      }
    }
  },

  ProductivityDashboard: {
    label: "Productivity Dashboard",
    icon: "phone",
    routes: {
      "/productivity-dashboard": {
        name: "/productivity-dashboard",
        label: "Productivity Dashboard",
        rbacEnabled: true,
        componentPath: "modules/productivity_dashboard/ProductivityDashboard",
        subRoutes: []
      }
    }
  },

  KPIDashboard: {
    label: "KPI Dashboard",
    icon: "chart-pie",
    routes: {
      "/kpi-dashboard": {
        name: "/kpi-dashboard",
        label: "KPI Dashboard",
        rbacEnabled: true,
        componentPath: "modules/kpi_dashboard/CallingKpi",
        subRoutes: [
          { key: "kpi-calling", name: "KPI Calling", order: 0, componentPath: "modules/kpi_dashboard/CallingKpi" },
          { key: "kpi-historical", name: "KPI Historical Dashboard", order: 1, componentPath: "modules/kpi_dashboard/KpiDashboardHistory" },
          { key: "medical-kpi", name: "Medical KPI", order: 2, componentPath: "modules/medical_kpi_dashboard/MedicalKpi" },
          { key: "complaint-kpi", name: "Complaint KPI", order: 3, componentPath: "modules/complaints_kpi_dashboard/ComplaintKpi" },
          { key: "call-attempts", name: "Call Attempts Dashboard", order: 4, componentPath: "modules/manager_call_dashboard/ManagerCallDashboard" }
        ]
      }
    }
  },

  LeadDashboard: {
    label: "Lead Dashboard",
    icon: "chart-pie",
    routes: {
      "/lead-dashboard": {
        name: "/lead-dashboard",
        label: "Lead Dashboard",
        rbacEnabled: true,
        componentPath: "modules/medrep_engagement/TodayEngagement",
        subRoutes: []
      }
    }
  },

  SalesDashboard: {
    label: "Sales Dashboard",
    icon: "chart-line",
    routes: {
      "/sales-dashboard": {
        name: "/sales-dashboard",
        label: "Sales Dashboard",
        rbacEnabled: true,
        componentPath: "pages/SalesDashboard",
        subRoutes: []
      }
    }
  },

  ProductManagement: {
    label: "Product Management",
    icon: "chart-line",
    routes: {
      "/product-management": {
        name: "/product-management",
        label: "Product Management",
        rbacEnabled: true,
        componentPath: "pages/ProductManagement",
        subRoutes: []
      }
    }
  },

  ClinicDashboard: {
    label: "Clinic Appointment",
    icon: "home",
    routes: {
      "/clinic-dashboard": {
        name: "/clinic-dashboard",
        label: "Clinic Dashboard",
        rbacEnabled: true,
        componentPath: "modules/clinic-management/ClinicsDashBoard",
        subRoutes: []
      }
    }
  },

  FinancialDashboard: {
    label: "Sales Charts",
    icon: "chart-line",
    routes: {
      "/financial-dashboard": {
        name: "/financial-dashboard",
        label: "Sales Charts",
        rbacEnabled: true,
        componentPath: "pages/FinanceDashboard",
        tabs: [
          { key: "sales-data", name: "Sales Data", order: 0 },
          { key: "cancel-data", name: "Cancel Data", order: 1 },
          { key: "rto-data", name: "RTO Data", order: 2 }
        ]
      }
    }
  },

  CheckoutCart: {
    label: "Checkout Cart",
    icon: "shopping-cart",
    routes: {
      "/checkout-success-rate": {
        name: "/checkout-success-rate",
        label: "Checkout Cart",
        rbacEnabled: true,
        componentPath: "pages/CheckoutSuccessRatePage",
        tabs: [
          { key: "payments-by-gateway", name: "Number Of Payments by Gateway", order: 0 },
          { key: "failure-rate", name: "Failure Rate for Gateway", order: 1 },
          { key: "payment-failure-reasons", name: "Reasons for Payment Failure", order: 2 },
          { key: "download", name: "Download", order: 3 }
        ]
      }
    }
  },

  // ===== ADDITIONAL ROUTES =====
  Search: {
    label: "Search",
    icon: "search",
    routes: {
      "/search": {
        name: "/search",
        label: "Search",
        rbacEnabled: true,
        componentPath: "pages/dispatchSearch/Search",
        subRoutes: []
      }
    }
  },

  IVRCall: {
    label: "IVR Calls",
    icon: "user",
    routes: {
      "/ivr-calls": {
        name: "/ivr-calls",
        label: "IVR Calls",
        rbacEnabled: true,
        componentPath: "pages/IvrCall",
        subRoutes: []
      }
    }
  },

  Community: {
    label: "Community",
    icon: "users",
    routes: {
      "/community": {
        name: "/community",
        label: "Community",
        rbacEnabled: true,
        componentPath: "pages/Community",
        tabs: [
          { key: "traya-community", name: "Traya Community", order: 0 },
          { key: "community-history", name: "Community History", order: 1 },
          { key: "engagements", name: "Engagements", order: 2 }
        ]
      }
    }
  },

  Warehouse: {
    label: "Warehouse",
    icon: "home",
    routes: {
      "/warehouse": {
        name: "/warehouse",
        label: "Warehouse",
        rbacEnabled: true,
        componentPath: "pages/Warehouse",
        tabs: [
          { key: "stock", name: "Stock", order: 0 },
          { key: "warehouse-inwards", name: "Warehouse Inwards", order: 1 },
          { key: "warehouse-outwards", name: "Warehouse OutWards", order: 2 },
          { key: "outward-intransit", name: "Outward Intransit", order: 3 },
          { key: "manual-updation", name: "Manual Updation", order: 4 },
          { key: "warehouse-supplier", name: "Warehouse Supplier", order: 5 },
          { key: "warehouse-report", name: "Warehouse Report", order: 6 }
        ]
      }
    }
  },

  Invoice: {
    label: "Invoice & Credit Note",
    icon: "file-alt",
    routes: {
      "/invoice": {
        name: "/invoice",
        label: "Invoice & Credit Note",
        rbacEnabled: true,
        componentPath: "pages/InvoiceGenerate",
        tabs: [
          { key: "invoice", name: "Generate Invoice", order: 0 },
          { key: "credit", name: "Generate Credit Note", order: 1 },
          { key: "download", name: "Download Invoice Report", order: 2 },
          { key: "downloadCaseOrderCount", name: "Cases Assignment Order Count Wise Report", order: 3 }
        ]
      }
    }
  },

  MiscOrders: {
    label: "Misc Orders",
    icon: "users",
    routes: {
      "/misc-orders": {
        name: "/misc-orders",
        label: "Misc Orders",
        rbacEnabled: true,
        componentPath: "pages/oda/components/ODA",
        tabs: [
          { key: "allocated-orders", name: "Allocated Orders", order: 0 },
          { key: "b2b-orders", name: "B2B Orders", order: 1 },
          { key: "flagged-invoice", name: "Flagged Invoice", order: 2 },
          { key: "flagged-orders", name: "Flagged Orders", order: 3 },
          { key: "international-orders", name: "International Orders", order: 4 },
          { key: "onhold-orders", name: "OnHold Orders", order: 5 },
          { key: "other-orders", name: "Other Orders", order: 6 },
          { key: "prozo-pending-orders", name: "Prozo Pending Orders", order: 7 },
          { key: "pending-for-shipping", name: "Pending for Shipping", order: 8 },
          { key: "delivery-flagged-orders", name: "Delivery Flagged Orders", order: 9 }
        ]
      }
    }
  },

  WhatsAppAnalytics: {
    label: "WhatsApp Analytics",
    icon: "comment",
    routes: {
      "/whatsapp-analytics": {
        name: "/whatsapp-analytics",
        label: "WhatsApp Analytics",
        rbacEnabled: true,
        componentPath: "modules/whatsapp_analytics/WhatsAppAnalyticsLayout",
        tabs: [
          { key: "overview", name: "Overview", order: 0 },
          { key: "insights", name: "Insights", order: 1 },
          { key: "comparison", name: "Comparison", order: 2 },
          { key: "templates", name: "Templates", order: 3 }
        ]
      }
    }
  },

  AutoQaAudit: {
    label: "Auto QA Audit",
    icon: "robot",
    routes: {
      "/qa-audit": {
        name: "/qa-audit",
        label: "Auto QA Audit",
        rbacEnabled: true,
        componentPath: "modules/qa_audit/QaAuditDashboard",
        defaultRoles: ["super_admin", "quality"],
        subRoutes: [
          { key: "evaluations", name: "Evaluations", order: 0, componentPath: "modules/qa_audit/QaAuditDashboard", defaultRoles: ["super_admin", "quality"] },
          {
            key: "convin-dispatch", name: "Convin Dispatch",
            order: 1, rbacEnabled: true, componentPath: "modules/qa_audit/ConvinDispatchPage",
            defaultRoles: ["super_admin", "quality"],
            tabs: [
              { key: "agents", name: "Agents", order: 0 },
              { key: "engagements", name: "Engagements", order: 1 }
            ]
          }
        ]
      }
    }
  },

  MasterTable: {
    label: "Master Table",
    icon: "table",
    routes: {
      "/manage-master-table": {
        name: "/manage-master-table",
        label: "Master Table",
        rbacEnabled: true,
        componentPath: "modules/master_table/MasterTable",
        tabs: [
          { key: "task-list", name: "Task List", order: 0 },
          { key: "coach-tip-list", name: "Coach Tip List", order: 1 },
          { key: "diet-plan-list", name: "Diet Plan List", order: 2 },
          { key: "home-page-tagline-list", name: "Home Page Tag Line List", order: 3 },
          { key: "reorder-kit-list", name: "Reorder Kit List", order: 4 },
          { key: "root-cause-list", name: "Root Cause List", order: 5 },
          { key: "root-cause-section-list", name: "Root Cause Section List", order: 6 },
          { key: "traya-doctor-list", name: "Traya Doctor List", order: 7 },
          { key: "draft-header-data-list", name: "Draft Header Data List", order: 8 },
          { key: "user-review-list", name: "User Review List", order: 9 },
          { key: "welcome-video-list", name: "Welcome Video List", order: 10 }
        ]
      }
    }
  },

  // ===== NEW VISIBLE GROUPS (April 2026) =====
  ConvinDashboard: {
    label: "Convin Dashboard",
    icon: "flag",
    routes: {
      "/covin-dashboard": {
        name: "/covin-dashboard",
        label: "Convin Dashboard",
        rbacEnabled: true,
        componentPath: "modules/covin_dashboard/CovinDashboard",
        featureFlag: "CONVIN_DASHBOARD_ACCESS",
        subRoutes: []
      }
    }
  },

  Allocation: {
    label: "Allocation",
    icon: "tasks",
    routes: {
      "/allocation": {
        name: "/allocation",
        label: "Allocation",
        rbacEnabled: true,
        subRoutes: [
          { key: "allocation-jobs", name: "Allocation Jobs", order: 0, componentPath: "pages/Allocation" },
          { key: "bulk-allocation", name: "Bulk Allocation", order: 1, componentPath: "pages/BulkDeactivate" }
        ]
      }
    }
  },

  SlotBookingLoggers: {
    label: "Slot Booking Loggers",
    icon: "user",
    routes: {
      "/slot-booking-loggers": {
        name: "/slot-booking-loggers",
        label: "Slot Booking Loggers",
        rbacEnabled: true,
        componentPath: "pages/SlotBookingLoggers",
        subRoutes: []
      }
    }
  },

  StoreCustomerAssignments: {
    label: "Store Customer Assignments",
    icon: "home",
    routes: {
      "/store-customer-assignment": {
        name: "/store-customer-assignment",
        label: "Store Customer Assignments",
        rbacEnabled: true,
        componentPath: "modules/store-customer-assignment/StoreCustomerAssignment",
        subRoutes: []
      }
    }
  },

  CMS: {
    label: "CMS",
    icon: "paper-plane",
    routes: {
      "/cms": {
        name: "/cms",
        label: "CMS",
        rbacEnabled: true,
        subRoutes: [
          { key: "manage-segments", name: "Manage Segments", order: 0, componentPath: "pages/ManageSegment" },
          { key: "manage-components", name: "Manage Components", order: 1, componentPath: "pages/ManageComponents" },
          { key: "manage-contents", name: "Manage Contents", order: 2, componentPath: "pages/ManageContents" },
          { key: "manage-tags", name: "Manage Tags", order: 3, componentPath: "pages/ManageTag" }
        ]
      }
    }
  },

  StoreVisit: {
    label: "Store Visit",
    icon: "users",
    routes: {
      "/store-visit": {
        name: "/store-visit",
        label: "Store Visit",
        rbacEnabled: true,
        componentPath: "pages/StoreVisit",
        subRoutes: []
      }
    }
  },

  Offline: {
    label: "Offline",
    icon: "chart-pie",
    routes: {
      "/offline": {
        name: "/offline",
        label: "Offline",
        rbacEnabled: false,
        subRoutes: [
          { key: "subscription-analytics", name: "Subscription Analytics", order: 0, rbacEnabled: false, componentPath: "pages/SubscriptionAnalytics" },
          { key: "doctor-consultation-refunds", name: "Doctor Consultation Refunds", order: 1, rbacEnabled: false, componentPath: "pages/DoctorConsultationRefund" },
          { key: "consultation-invoices", name: "Consultation Invoices", order: 2, rbacEnabled: false, componentPath: "pages/ConsultationInvoices" }
        ]
      }
    }
  },

  CallDashboard: {
    label: "Call Dashboard",
    icon: "chart-bar",
    routes: {
      "/call-dashboard": {
        name: "/call-dashboard",
        label: "Call Dashboard",
        rbacEnabled: true,
        componentPath: "pages/CallDashboard",
        subRoutes: []
      }
    }
  },

  SSOTDashboard: {
    label: "SSOT Dashboard",
    icon: "home",
    routes: {
      "/ssot": {
        name: "/ssot",
        label: "SSOT Dashboard",
        rbacEnabled: true,
        componentPath: "modules/user_management/SSOT",
        subRoutes: []
      }
    }
  },

  AttendanceTracker: {
    label: "Attendance Tracker",
    icon: "paper-plane",
    routes: {
      "/attendance": {
        name: "/attendance",
        label: "Attendance Tracker",
        rbacEnabled: true,
        subRoutes: [
          { key: "attendance-tracker", name: "Attendance Tracker", order: 0, componentPath: "modules/attendence_leave_tracker/AttendanceTracker" },
          { key: "leave-tracker", name: "Leave Tracker", order: 1, componentPath: "modules/attendence_leave_tracker/LeaveTracker" },
          { key: "approvals-tracker", name: "Approvals Tracker", order: 2, componentPath: "modules/attendence_leave_tracker/ApprovalsTracker" }
        ]
      }
    }
  },

  // ===== NEW VISIBLE GROUPS (April 7, 2026 — Role-Route RBAC Fix) =====
  NextCall: {
    label: "Next Calls",
    icon: "phone",
    routes: {
      "/next-call": {
        name: "/next-call",
        label: "Next Calls",
        rbacEnabled: true,
        componentPath: "modules/nextcall",
        subRoutes: []
      }
    }
  },

  NextCallV2: {
    label: "Next Calls V2",
    icon: "phone",
    routes: {
      "/next-call-v2": {
        name: "/next-call-v2",
        label: "Next Calls V2",
        rbacEnabled: true,
        componentPath: "modules/nextcall/NextCallV2",
        subRoutes: []
      }
    }
  },

  CustomerActiveChats: {
    label: "Customer Active Chats",
    icon: "comments",
    routes: {
      "/customer-chats": {
        name: "/customer-chats",
        label: "Customer Active Chats",
        rbacEnabled: true,
        componentPath: "pages/CustomerChat",
        subRoutes: []
      }
    }
  },

  UtmCampaign: {
    label: "Utm Campaign",
    icon: "bullhorn",
    routes: {
      "/utm-campaign": {
        name: "/utm-campaign",
        label: "Utm Campaign",
        rbacEnabled: true,
        componentPath: "pages/UtmCampaign",
        subRoutes: []
      }
    }
  },

  IncentivesDashboard: {
    label: "Incentives Dashboard",
    icon: "award",
    routes: {
      "/incentives": {
        name: "/incentives",
        label: "Incentives Dashboard",
        rbacEnabled: true,
        subRoutes: [
          { key: "calling-incentives", name: "Calling Incentives", order: 0, componentPath: "modules/allIncentiveDashboard/callingIncentiveDashboard/CallingIncentives" },
          { key: "medical-incentives", name: "Medical Incentives", order: 1, componentPath: "modules/medical_incentives/MedicalIncentives" },
          { key: "complaints-incentives", name: "Complaints Incentives", order: 2, componentPath: "modules/complaints_incentives/ComplaintIncentives" }
        ]
      }
    }
  },

  Actionable: {
    label: "Actionable",
    icon: "tasks",
    routes: {
      "/actionable-doctor": {
        name: "/actionable-doctor",
        label: "Actionable",
        rbacEnabled: true,
        componentPath: "modules/medrep_engagement/TodayEngagement",
        subRoutes: []
      }
    }
  },

  MyAppointments: {
    label: "My Appointments",
    icon: "calendar-check",
    routes: {
      "/my-appointments": {
        name: "/my-appointments",
        label: "My Appointments",
        rbacEnabled: true,
        componentPath: "pages/MyAppointment",
        subRoutes: []
      }
    }
  },

  StoreOrders: {
    label: "Store Orders",
    icon: "store",
    routes: {
      "/clinic-home": {
        name: "/clinic-home",
        label: "Store Orders",
        rbacEnabled: true,
        componentPath: "pages/AllopathyDoctorDashboard",
        subRoutes: []
      }
    }
  },

  DoctorAppointment: {
    label: "Doctor Appointment",
    icon: "user-md",
    routes: {
      "/receptioninst-dashboard": {
        name: "/receptioninst-dashboard",
        label: "Doctor Appointment",
        rbacEnabled: true,
        componentPath: "modules/medrep_engagement/ReciptioninstTodayEngagement",
        subRoutes: []
      }
    }
  },

  RevenueTracking: {
    label: "Revenue Tracking",
    icon: "chart-line",
    routes: {
      "/revenue-tracking": {
        name: "/revenue-tracking",
        label: "Revenue Tracking",
        rbacEnabled: true,
        componentPath: "pages/RevenueTracker",
        subRoutes: []
      }
    }
  },

  Zoho: {
    label: "Zoho",
    icon: "sync",
    routes: {
      "/zoho": {
        name: "/zoho",
        label: "Zoho",
        rbacEnabled: true,
        componentPath: "modules/zoho/Zoho",
        subRoutes: []
      }
    }
  },

  TicketGroupManagement: {
    label: "Ticket Group Management",
    icon: "object-group",
    routes: {
      "/ticket-group-management": {
        name: "/ticket-group-management",
        label: "Ticket Group Management",
        rbacEnabled: true,
        componentPath: "pages/TicketGroups",
        tabs: [
          { key: "subcategories", name: "Subcategories", order: 0 },
          { key: "groups", name: "Groups", order: 1 },
          { key: "groups-activity-logs", name: "Groups Activity Logs", order: 2 }
        ]
      }
    }
  },

  NPSDashboard: {
    label: "NPS Dashboard",
    icon: "chart-area",
    routes: {
      "/nps-dashboard": {
        name: "/nps-dashboard",
        label: "NPS Dashboard",
        rbacEnabled: true,
        subRoutes: [
          { key: "nps-analysis", name: "NPS Dashboard", order: 0, componentPath: "pages/NpsAnalysisDashboardPage" },
          { key: "nps-v2", name: "NPS V2", order: 1, componentPath: "pages/NpsV2Page" }
        ]
      }
    }
  },

  // ===== SHARED ROUTES (hidden from sidebar) =====
  SharedRoutes: {
    label: "Shared Routes",
    icon: "link",
    hidden: true,
    routes: {
      "/next-call/:id": { name: "/next-call/:id", label: "Next Call Details", rbacEnabled: false, componentPath: "modules/dashboards/actionables/LeadDetails", props: { nextCall: true } },
      "/next-call-v2/:id": { name: "/next-call-v2/:id", label: "Next Call V2 Details", rbacEnabled: false, componentPath: "modules/dashboards/actionables_v2/LeadDetails", props: { nextCall: true } },
      "/lead-details/:id": { name: "/lead-details/:id", label: "Lead Details", rbacEnabled: false, componentPath: "modules/dashboards/actionables/LeadDetails" },
      "/lead-details-v2/:id": { name: "/lead-details-v2/:id", label: "Lead Details V2", rbacEnabled: false, componentPath: "modules/dashboards/actionables_v2/LeadDetails" },
      "/dashboard/home": { name: "/dashboard/home", label: "Dashboard Home", rbacEnabled: false, componentPath: "pages/Home" },
      "/dashboard/home/assigned-orders": { name: "/dashboard/home/assigned-orders", label: "Assigned Orders", rbacEnabled: false, componentPath: "modules/dashboards/home/CustomerList", props: { assignedOrder: true } },
      "/case-management/all-cases-tag": { name: "/case-management/all-cases-tag", label: "All Cases Tags", rbacEnabled: false, componentPath: "modules/case_management/AllLeadsTag" },
      "/case-management/sales-management": { name: "/case-management/sales-management", label: "Sales Management", rbacEnabled: false, componentPath: "modules/case_management/SalesManagement" },
      "/today-engagement/:id": { name: "/today-engagement/:id", label: "Today Engagement", rbacEnabled: false, componentPath: "modules/medrep_engagement/MedRepEngagement" },
      "/dashboard/revenue-report": { name: "/dashboard/revenue-report", label: "Revenue Report", rbacEnabled: false, componentPath: "modules/dashboards/revenue-report" },
      "/dashboard/actionables": { name: "/dashboard/actionables", label: "Actionables", rbacEnabled: false, componentPath: "pages/ActionableList" },
      "/dashboard/engagement": { name: "/dashboard/engagement", label: "Engagement", rbacEnabled: false, componentPath: "pages/Engagement" },
      "/ivrCall": { name: "/ivrCall", label: "IVR Call", rbacEnabled: false, componentPath: "pages/IvrCall" },
      "/lead-dashboard": { name: "/lead-dashboard", label: "Lead Dashboard", rbacEnabled: false, componentPath: "modules/medrep_engagement/TodayEngagement" },
      "/home": { name: "/home", label: "Home", rbacEnabled: false, componentPath: "pages/Home" },
      "/home/:id": { name: "/home/:id", label: "Home Detail", rbacEnabled: false, componentPath: "pages/DashboardHome" },
      "/recording-feedback/:engagementTrackingId": { name: "/recording-feedback/:engagementTrackingId", label: "Recording Feedback Single", rbacEnabled: false, componentPath: "modules/qa_integration/QaRecordingFeedback" },
      "/medical-esaclation-tickets": { name: "/medical-esaclation-tickets", label: "Medical Escalation Tickets (Legacy)", rbacEnabled: false, componentPath: "modules/my_tickets/MedicalEscalationTickets" },
      "/order-management/all-orders": { name: "/order-management/all-orders", label: "All Orders", rbacEnabled: false, componentPath: "modules/order_management/AllOrdersManagement" }
    }
  },

  // ===== ORDER ROUTES (hidden from sidebar) =====
  OrderRoutes: {
    label: "Order Routes",
    icon: "shopping-cart",
    hidden: true,
    routes: {
      "/order-management/update_order": { name: "/order-management/update_order", label: "Update Orders", rbacEnabled: true, componentPath: "pages/UpdateOrders" },
      "/order-management/open_orders": { name: "/order-management/open_orders", label: "Open Orders", rbacEnabled: true, componentPath: "pages/OpenOrders" },
      "/order-management/recent_orders": { name: "/order-management/recent_orders", label: "Recent Orders", rbacEnabled: true, componentPath: "pages/RecentOrders" },
      "/order-management/assigned-doctors": { name: "/order-management/assigned-doctors", label: "Assigned Doctors", rbacEnabled: true, componentPath: "pages/AssignedDoctors" },
      "/order-management/ghost-orders": { name: "/order-management/ghost-orders", label: "Ghost Orders", rbacEnabled: true, componentPath: "pages/GhostOrders" },
      "/order-management/cod-to-prepaid": { name: "/order-management/cod-to-prepaid", label: "COD to Prepaid", rbacEnabled: true, componentPath: "pages/CodToPrepaid" },
      "/order-management/pre-dispatch-cod-to-prepaid": { name: "/order-management/pre-dispatch-cod-to-prepaid", label: "Pre Dispatch COD to Prepaid", rbacEnabled: true, componentPath: "pages/PreDispatchCodToPrepaid" },
      "/order-management/order-case-details/:orderDisplayId": { name: "/order-management/order-case-details/:orderDisplayId", label: "Order Case Details", rbacEnabled: true, componentPath: "pages/OrderCaseDetails" },
      "/order-management/offline-orders": { name: "/order-management/offline-orders", label: "Offline Orders", rbacEnabled: true, componentPath: "modules/offline_orders/OfflineOrders" },
      "/order-management/refund-management": { name: "/order-management/refund-management", label: "Refund Management", rbacEnabled: true, componentPath: "modules/refund_management/RefundManagement" },
      "/order-management/return-orders": { name: "/order-management/return-orders", label: "Return Orders", rbacEnabled: true, componentPath: "modules/order_management/ReturnOrders" },
      "/order-management/crm-prepaid": { name: "/order-management/crm-prepaid", label: "CRM Prepaid", rbacEnabled: true, componentPath: "pages/CrmPrepaid" }
    }
  },
  // NOTE: OrderRoutes stays as hidden group for direct route registration (React Router).
  // The sidebar children are defined in AdminOrderManagement.subRoutes above.

  // ===== DASHBOARD ROUTES (hidden from sidebar) =====
  DashboardRoutes: {
    label: "Dashboard Routes",
    icon: "chart-bar",
    hidden: true,
    routes: {
      "/call-stats": { name: "/call-stats", label: "Call Stats", rbacEnabled: true, componentPath: "pages/CallStats" },
      "/funnel-dashboard": { name: "/funnel-dashboard", label: "Funnel Dashboard", rbacEnabled: true, componentPath: "modules/funnel_dashboard" },
      "/report-dashboard": { name: "/report-dashboard", label: "Report Dashboard", rbacEnabled: true, componentPath: "modules/Shopify_dashboard/ReportDashboard" },
    }
  },

  // ===== UMAMI ANALYTICS =====
  funnelAnalytics: {
    label: "Funnel Analytics",
    icon: "chart-line",
    routes: {
      "/funnel-analytics": {
        name: "/funnel-analytics",
        label: "Funnel Analytics",
        rbacEnabled: false,
        componentPath: "modules/umami_funnel_dashboard"
      }
    }
  },

  // ===== ENGAGEMENT DASHBOARDS =====
  EngagementDashboards: {
    label: "Engagement Dashboards",
    icon: "chart-area",
    routes: {
      "/engagement-dashboards": {
        name: "/engagement-dashboards",
        label: "Engagement Dashboards",
        rbacEnabled: true,
        subRoutes: [
          { key: "outbound-summary", name: "Outbound Summary", order: 0, componentPath: "pages/OutboundSummaryPage" },
          { key: "engagement-details", name: "Engagement Analytics", order: 1, componentPath: "pages/EngagementDetailsPage" },
          { key: "slot-pendency", name: "Slot Pendency", order: 2, componentPath: "pages/SlotPendencyPage" },
          { key: "daily-pendency", name: "Overall Pendency Dashboard", order: 3, componentPath: "pages/DailyPendencyPage" },
          { key: "agent-login-report", name: "Agent Login Report", order: 4, componentPath: "pages/AgentLoginReportPage" },
          { key: "agent-performance", name: "Agent Performance", order: 5, componentPath: "pages/AgentPerformancePage" },
          { key: "lead-call-report", name: "Lead Call Report", order: 6, componentPath: "modules/engagement_report/lead_call_report/LeadCallReport" },
          { key: "same-coach", name: "Same Coach Analytics", order: 7, componentPath: "pages/SameCoachAnalyticsPage" },
          { key: "slot-availability", name: "Slot Availability Dashboard", order: 8, componentPath: "pages/SlotAvailabilityPage" },
          { key: "agent-performance-due", name: "Agent Performance (Due Date)", order: 9, componentPath: "pages/AgentPerformanceDuePage" }
        ]
      }
    }
  },

  // ===== ENGAGEMENT DASHBOARDS EXTRA ROUTES (hidden from sidebar) =====
  EngagementDashboardsExtra: {
    label: "Engagement Dashboards Extra",
    icon: "chart-area",
    hidden: true,
    routes: {
      "/engagement-report/summary": { name: "/engagement-report/summary", label: "Engagement Summary", rbacEnabled: true, componentPath: "pages/EngagementSummary" }
    }
  },

  // ===== QA & QUALITY ROUTES (hidden from sidebar) =====
  QARoutes: {
    label: "QA & Quality",
    icon: "clipboard-check",
    hidden: true,
    routes: {
      "/main-content": { name: "/main-content", label: "QA Main Content", rbacEnabled: true, componentPath: "modules/qa_integration/MainContent" },
      "/my-work": { name: "/my-work", label: "My Work", rbacEnabled: true, componentPath: "modules/qa_integration/MyWork" },
      "/recording-feedback/:engagementTrackingId/:engagementCoachUserId": { name: "/recording-feedback/:engagementTrackingId/:engagementCoachUserId", label: "Recording Feedback", rbacEnabled: true, componentPath: "modules/qa_integration/QaRecordingFeedback" },
      "/location-view": { name: "/location-view", label: "Location View", rbacEnabled: true, componentPath: "modules/qa_integration/LocationView" },
      "/coach-view": { name: "/coach-view", label: "Coach View", rbacEnabled: true, componentPath: "modules/qa_integration/CoachView" },
      "/qa-view": { name: "/qa-view", label: "QA View", rbacEnabled: true, componentPath: "modules/qa_integration/QAView" },
      "/qa-questions": { name: "/qa-questions", label: "QA Questions", rbacEnabled: true, componentPath: "modules/qa_questions/QA_questions" }
    }
  },

  // ===== INCENTIVES ROUTES (hidden from sidebar) =====
  IncentivesRoutes: {
    label: "Incentives",
    icon: "award",
    hidden: true,
    routes: {
      "/incentives-slab": { name: "/incentives-slab", label: "Incentives Slab", rbacEnabled: true, componentPath: "modules/allIncentiveDashboard/incentiveSlabDashboard/IncentiveSlab" },
      "/incentives-achievedScore": { name: "/incentives-achievedScore", label: "Achieved Score", rbacEnabled: true, componentPath: "modules/allIncentiveDashboard/incentiveAcheivedScoreDashboard/AchievedScore" },
      "/weightage-target": { name: "/weightage-target", label: "Weightage & Target", rbacEnabled: true, componentPath: "modules/allIncentiveDashboard/weightageAndTargetDashboard/WeightageAndTarget" }
    }
  },

  // ===== MISCELLANEOUS ROUTES (hidden from sidebar) =====
  MiscRoutes: {
    label: "Other Routes",
    icon: "ellipsis-h",
    hidden: true,
    routes: {
      "/myteam": { name: "/myteam", label: "My Team", rbacEnabled: true, componentPath: "pages/MyTeam" },
      "/manage-weekly-schedules": { name: "/manage-weekly-schedules", label: "Manage Weekly Schedules", rbacEnabled: true, componentPath: "pages/ManageWeekly" },
      "/medRep-engagement": { name: "/medRep-engagement", label: "MedRep Engagements", rbacEnabled: true, componentPath: "modules/manage_weekly-schedule/MedRepEngagements" },
      "/idealtimecalculator": { name: "/idealtimecalculator", label: "Ideal Time Calculator", rbacEnabled: true, componentPath: "modules/ideal_time_calculator/IdeaDashboard" },
      "/pincode-management": { name: "/pincode-management", label: "Pincode Management", rbacEnabled: true, componentPath: "modules/pincode_management/PincodeManagement" },
      "/community-details": { name: "/community-details", label: "Community Details", rbacEnabled: true, componentPath: "modules/community/CommunityPage" },
      "/manage-segment/segmentDetail/:segmentId/:segmentName": { name: "/manage-segment/segmentDetail/:segmentId/:segmentName", label: "Segment Detail", rbacEnabled: true, componentPath: "modules/manage_segment/SegmentDetail" },
      "/manage-tags/tagDetail/:tagId/:tagMasterName": { name: "/manage-tags/tagDetail/:tagId/:tagMasterName", label: "Tag Detail", rbacEnabled: true, componentPath: "modules/manage_tags/TagDetail" },
      "/warehouse-pincode-mapping": { name: "/warehouse-pincode-mapping", label: "Warehouse Pincode Mapping", rbacEnabled: true, componentPath: "pages/WarehousePincodeMapping" },
      "/uni_commerce_dashboard": { name: "/uni_commerce_dashboard", label: "UniCommerce Dashboard", rbacEnabled: true, componentPath: "modules/unicommerce/FailedUniOrdersTable" },
      "/group-management": { name: "/group-management", label: "Group Management", rbacEnabled: true, componentPath: "pages/GroupManagement" },
      "/refund-tracking": { name: "/refund-tracking", label: "Refund Tracking", rbacEnabled: true, componentPath: "pages/RefundTracking" },
      "/na-ndr-orders": { name: "/na-ndr-orders", label: "NA NDR Orders", rbacEnabled: true, componentPath: "pages/NaNdrOrders" },
      "/configure/offline-bulk-kit-cohort-config": { name: "/configure/offline-bulk-kit-cohort-config", label: "Offline Bulk Kit Cohort Config", rbacEnabled: true, componentPath: "pages/OfflineBulkKitCohortConfig" },
      "/order-tracking-service": { name: "/order-tracking-service", label: "Order Tracking Service", rbacEnabled: true, componentPath: "modules/Ots_mangment/OTSManagment", featureFlag: "OTS_DELHIVERY_ACCESS" },
      "/ndr-mapper": { name: "/ndr-mapper", label: "NDR Mapper", rbacEnabled: true, componentPath: "modules/Ndr_management/NDRManagement", featureFlag: "OTS_DELHIVERY_ACCESS" },
      "/ndr-mapper-master": { name: "/ndr-mapper-master", label: "NDR Mapper Master", rbacEnabled: true, componentPath: "modules/Ndr_management_master/NDRManagementMaster", featureFlag: "OTS_DELHIVERY_ACCESS" },
      "/bulk-upload/delhivery": { name: "/bulk-upload/delhivery", label: "Bulk Upload Delhivery", rbacEnabled: true, componentPath: "modules/third_party_upload/UploadCsvFutwork", featureFlag: "OTS_DELHIVERY_ACCESS" },
      "/approve-prescriptions": { name: "/approve-prescriptions", label: "Approve Prescriptions", rbacEnabled: true, componentPath: "pages/ApprovePrescription" },
      "/transactions-dashboard": { name: "/transactions-dashboard", label: "Transactions Dashboard", rbacEnabled: true, componentPath: "modules/payment_gateway/PayementGateway" },
      "/mtd-report": { name: "/mtd-report", label: "MTD Report", rbacEnabled: true, componentPath: "modules/mtd_report/MonthReport", featureFlag: "MTD_REPORT_ACCESS" },
      "/customerFeedback": { name: "/customerFeedback", label: "Customer Feedback Page", rbacEnabled: true, componentPath: "pages/CustomerFeedback" },
      "/medical-kpi": { name: "/medical-kpi", label: "Medical KPI", rbacEnabled: true, componentPath: "modules/medical_kpi_dashboard/MedicalKpi" },
      "/complaint-kpi": { name: "/complaint-kpi", label: "Complaint KPI", rbacEnabled: true, componentPath: "modules/complaints_kpi_dashboard/ComplaintKpi" },
      "/kpi-calling": { name: "/kpi-calling", label: "KPI Calling", rbacEnabled: true, componentPath: "modules/kpi_dashboard/CallingKpi", featureFlag: "KPI_DASHBOARD_ACCESS" },
      "/kpi-historical-dashboard": { name: "/kpi-historical-dashboard", label: "KPI Historical", rbacEnabled: true, componentPath: "modules/kpi_dashboard/KpiDashboardHistory", featureFlag: "KPI_DASHBOARD_ACCESS" },
      "/new-sales-dashboard": { name: "/new-sales-dashboard", label: "New Sales Dashboard", rbacEnabled: true, componentPath: "pages/NewSalesDashboard" },
      "/order-dashboard": { name: "/order-dashboard", label: "Order Dashboard", rbacEnabled: true, componentPath: "pages/OrderDashboard" },
      "/manager-call-attempts": { name: "/manager-call-attempts", label: "Manager Call Attempts", rbacEnabled: true, componentPath: "modules/manager_call_dashboard/ManagerCallDashboard" },
      "/unshipped-orders": { name: "/unshipped-orders", label: "Unshipped Orders", rbacEnabled: true, componentPath: "pages/UnshippedOrders" },
      "/control-tower": { name: "/control-tower", label: "Control Tower", rbacEnabled: true, componentPath: "pages/ControlTower" }
    }
  }
};

/**
 * Convert config to permission structure format
 * Used for initializing RBAC Management
 */
export const convertConfigToPermissions = () => {
  const permissions = {};
  let groupOrder = 0;

  Object.entries(ROUTES_TAB_CONFIG).forEach(([groupName, groupData]) => {
    permissions[groupName] = {
      id: `group-${groupName.toLowerCase()}`,
      groupKey: groupName.toLowerCase(),
      icon: groupData.icon || '',
      hidden: groupData.hidden || false,
      label: groupData.label || groupName,
      allowed: true,
      order: groupOrder++,
      routes: {}
    };

    Object.entries(groupData.routes).forEach(([routeKey, routeData]) => {
      const routePath = routeKey; // e.g., "/configure", "/my-tickets"

      // Scoped role gating: only routes that explicitly opt in via
      // rbacEnabled === true AND a non-empty defaultRoles array are gated
      // by the current user's role. All other routes keep the historic
      // allowed: true fallback behavior (no regression for non-opted routes).
      const isRoleGated = routeData.rbacEnabled === true
        && Array.isArray(routeData.defaultRoles)
        && routeData.defaultRoles.length > 0;
      const routeAllowed = isRoleGated
        ? routeData.defaultRoles.includes(getCurrentUserRole())
        : true;

      permissions[groupName].routes[routeKey] = {
        id: `route-${routeKey.replace(/\//g, '-')}`,
        name: routeData.label || routeData.name,
        routeKey: routeKey,
        allowed: routeAllowed,
        rbacEnabled: routeData.rbacEnabled !== undefined ? routeData.rbacEnabled : false,
        componentPath: routeData.componentPath || '',
        props: routeData.props || {},
        featureFlag: routeData.featureFlag || null,
        path: routePath,
        order: 0,
        tabs: {},
        subRoutes: {}
      };

      // Add tabs with auto-generated paths
      if (routeData.tabs && Array.isArray(routeData.tabs)) {
        routeData.tabs.forEach((tab) => {
          const tabPath = generatePath(routePath, tab.key, 'tab');
          permissions[groupName].routes[routeKey].tabs[tab.key] = {
            id: `tab-${tab.key}`,
            name: tab.name,
            allowed: true,
            path: tabPath,
            order: tab.order
          };

          // Add nested sub-tabs within tabs
          if (tab.tabs && Array.isArray(tab.tabs)) {
            permissions[groupName].routes[routeKey].tabs[tab.key].tabs = {};
            tab.tabs.forEach((subTab) => {
              const subTabPath = generatePath(routePath, subTab.key, 'subTab', tab.key);
              permissions[groupName].routes[routeKey].tabs[tab.key].tabs[subTab.key] = {
                id: `tab-${subTab.key}`,
                name: subTab.name,
                allowed: true,
                path: subTabPath,
                order: subTab.order
              };
            });
          }
        });
      }

      // Add subroutes with auto-generated paths
      if (routeData.subRoutes && Array.isArray(routeData.subRoutes)) {
        routeData.subRoutes.forEach((subRoute) => {
          const subRoutePath = generatePath(routePath, subRoute.key, 'subRoute');
          permissions[groupName].routes[routeKey].subRoutes[subRoute.key] = {
            id: `subroute-${subRoute.key}`,
            name: subRoute.name,
            allowed: true,
            path: subRoutePath,
            order: subRoute.order,
            rbacEnabled: subRoute.rbacEnabled !== undefined ? subRoute.rbacEnabled : false,
            sidebarHidden: subRoute.sidebarHidden || false,
            componentPath: subRoute.componentPath || '',
            props: subRoute.props || {},
            featureFlag: subRoute.featureFlag || null,
            tabs: {}
          };

          // Add nested tabs on subroutes
          if (subRoute.tabs && Array.isArray(subRoute.tabs)) {
            subRoute.tabs.forEach((tab) => {
              const tabPath = generatePath(subRoutePath, tab.key, 'tab');
              permissions[groupName].routes[routeKey].subRoutes[subRoute.key].tabs[tab.key] = {
                id: `tab-${tab.key}`,
                name: tab.name,
                allowed: true,
                path: tabPath,
                order: tab.order
              };
            });
          }
        });
      }
    });
  });

  return permissions;
};

/**
 * Create empty role permission template
 * Returns permission structure with ALL routes/tabs/subroutes disabled (allowed: false)
 * Used as starting point when creating new role permissions
 * Admin can then selectively enable only the features needed for that role
 */
export const createEmptyRolePermissionTemplate = () => {
  const permissions = {};
  let groupOrder = 0;

  Object.entries(ROUTES_TAB_CONFIG).forEach(([groupName, groupData]) => {
    permissions[groupName] = {
      id: `group-${groupName.toLowerCase()}`,
      groupKey: groupName.toLowerCase(),
      icon: groupData.icon || '',
      hidden: groupData.hidden || false,
      label: groupData.label || groupName,
      allowed: false,  // ← Empty template: groups disabled
      order: groupOrder++,
      routes: {}
    };

    Object.entries(groupData.routes).forEach(([routeKey, routeData]) => {
      const routePath = routeKey;

      permissions[groupName].routes[routeKey] = {
        id: `route-${routeKey.replace(/\//g, '-')}`,
        name: routeData.label || routeData.name,
        routeKey: routeKey,
        allowed: false,  // ← Empty template: routes disabled
        rbacEnabled: routeData.rbacEnabled !== undefined ? routeData.rbacEnabled : false,
        componentPath: routeData.componentPath || '',
        props: routeData.props || {},
        featureFlag: routeData.featureFlag || null,
        path: routePath,
        order: 0,
        tabs: {},
        subRoutes: {}
      };

      // Add tabs (all disabled) with auto-generated paths
      if (routeData.tabs && Array.isArray(routeData.tabs)) {
        routeData.tabs.forEach((tab) => {
          const tabPath = generatePath(routePath, tab.key, 'tab');
          permissions[groupName].routes[routeKey].tabs[tab.key] = {
            id: `tab-${tab.key}`,
            name: tab.name,
            allowed: false,  // ← Empty template: tabs disabled
            path: tabPath,
            order: tab.order
          };

          // Add nested sub-tabs within tabs (all disabled)
          if (tab.tabs && Array.isArray(tab.tabs)) {
            permissions[groupName].routes[routeKey].tabs[tab.key].tabs = {};
            tab.tabs.forEach((subTab) => {
              const subTabPath = generatePath(routePath, subTab.key, 'subTab', tab.key);
              permissions[groupName].routes[routeKey].tabs[tab.key].tabs[subTab.key] = {
                id: `tab-${subTab.key}`,
                name: subTab.name,
                allowed: false,  // ← Empty template: sub-tabs disabled
                path: subTabPath,
                order: subTab.order
              };
            });
          }
        });
      }

      // Add subroutes (all disabled) with auto-generated paths
      if (routeData.subRoutes && Array.isArray(routeData.subRoutes)) {
        routeData.subRoutes.forEach((subRoute) => {
          const subRoutePath = generatePath(routePath, subRoute.key, 'subRoute');
          permissions[groupName].routes[routeKey].subRoutes[subRoute.key] = {
            id: `subroute-${subRoute.key}`,
            name: subRoute.name,
            allowed: false,  // ← Empty template: subroutes disabled
            path: subRoutePath,
            order: subRoute.order,
            rbacEnabled: subRoute.rbacEnabled !== undefined ? subRoute.rbacEnabled : false,
            sidebarHidden: subRoute.sidebarHidden || false,
            componentPath: subRoute.componentPath || '',
            props: subRoute.props || {},
            featureFlag: subRoute.featureFlag || null,
            tabs: {}
          };

          // Add nested tabs if subroute has them (all disabled)
          if (subRoute.tabs && Array.isArray(subRoute.tabs)) {
            subRoute.tabs.forEach((tab) => {
              const tabPath = generatePath(subRoutePath, tab.key, 'tab');
              permissions[groupName].routes[routeKey].subRoutes[subRoute.key].tabs[tab.key] = {
                id: `tab-${tab.key}`,
                name: tab.name,
                allowed: false,  // ← Empty template: tabs disabled
                path: tabPath,
                order: tab.order
              };
            });
          }
        });
      }
    });
  });

  return permissions;
};

/**
 * Get all route groups
 */
export const getAllRouteGroups = () => {
  return Object.entries(ROUTES_TAB_CONFIG).map(([key, data]) => ({
    key,
    ...data
  }));
};

/**
 * Build sidebar navigation routes from routeTabConfig + permissions.
 * Shape matches what NavbarVerticalMenu expects:
 *   { name, icon, to?, children?: [{ name, to, active }] }
 *
 * @param {object} permissions - Route permissions from store (merged with defaults)
 * @param {string[]} groupOrder - Group ordering from route order config
 * @returns {Array} Sidebar route objects
 */
export const buildSidebarRoutes = (permissions, groupOrder = []) => {
  if (!permissions || Object.keys(permissions).length === 0) {
    return [];
  }

  // Determine group ordering: use provided order, then fall back to config order
  const allGroups = Object.keys(permissions).filter(g => g !== '__settings');
  const orderedGroups = groupOrder.length > 0
    ? [...new Set([...groupOrder.filter(g => allGroups.includes(g)), ...allGroups])]
    : allGroups;

  const sidebarRoutes = [];

  orderedGroups.forEach(groupName => {
    const groupData = permissions[groupName];
    if (!groupData?.routes) return;

    // Skip hidden groups — they provide routes but should NOT appear in sidebar
    // ONLY check config's hidden flag (source of truth), NOT DB/API data which may be stale
    const configGroup = ROUTES_TAB_CONFIG[groupName];
    if (configGroup?.hidden) return;

    const groupIcon = groupData.icon || configGroup?.icon || 'database';

    Object.entries(groupData.routes).forEach(([routeKey, routeData]) => {
      // If route not allowed, skip (works for both RBAC and non-RBAC routes)
      if (routeData.allowed === false) return;

      const hasSubRoutes = routeData.subRoutes && Object.keys(routeData.subRoutes).length > 0;

      if (hasSubRoutes) {
        // Parent with children (subroute-based navigation)
        // Sort by explicit order field if present (JSONB doesn't preserve key order)
        const sortedSubEntries = Object.entries(routeData.subRoutes)
          .sort(([, a], [, b]) => (a.order ?? 999) - (b.order ?? 999));
        const children = [];
        sortedSubEntries.forEach(([subKey, subData]) => {
          // Skip subroutes flagged as sidebar-hidden (e.g. virtual RBAC-only entries
          // like `lead-details` that exist for the RBAC UI but not the left nav).
          if (subData.sidebarHidden) return;
          if (subData.rbacEnabled && subData.allowed === false) return;
          let subPath = subData.path;

          // If subroute has tabs, append first allowed tab as query param
          if (subData.tabs && Object.keys(subData.tabs).length > 0) {
            const sortedTabs = Object.entries(subData.tabs)
              .filter(([, t]) => t.allowed !== false)
              .sort(([, a], [, b]) => (a.order ?? 999) - (b.order ?? 999));
            if (sortedTabs.length > 0) {
              subPath = `${subPath}?tab=${sortedTabs[0][0]}`;
            }
          }

          children.push({
            name: subData.name || subKey,
            to: subPath,
            active: false,
          });
        });

        if (children.length > 0) {
          sidebarRoutes.push({
            name: groupData.label || ROUTES_TAB_CONFIG[groupName]?.label || groupName,
            icon: groupIcon,
            children,
            _groupKey: groupName,
            _routePath: routeKey,
          });
        }
      } else {
        // Leaf item (tabs-based or standalone)
        let toPath = routeData.path || routeKey;

        // If route has tabs, append first allowed tab as query param
        if (routeData.tabs && Object.keys(routeData.tabs).length > 0) {
          const sortedTabs = Object.entries(routeData.tabs)
            .filter(([, t]) => t.allowed !== false)
            .sort(([, a], [, b]) => (a.order ?? 999) - (b.order ?? 999));
          if (sortedTabs.length > 0) {
            toPath = `${toPath}?tab=${sortedTabs[0][0]}`;
          }
        }

        sidebarRoutes.push({
          name: groupData.label || ROUTES_TAB_CONFIG[groupName]?.label || groupName,
          icon: groupIcon,
          to: toPath,
          _groupKey: groupName,
          _routePath: routeKey,
        });
      }
    });
  });

  return sidebarRoutes;
};
